import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  BookOpen, 
  FolderOpen, 
  CreditCard,
  Plus,
  ArrowRight,
  Loader2,
  TrendingUp,
  TrendingDown,
  UserPlus,
  Handshake
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentStudents, setRecentStudents] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, studentsRes] = await Promise.all([
          axios.get(`${API_URL}/dashboard/stats`),
          axios.get(`${API_URL}/students?page=1&per_page=5`),
        ]);
        setStats(statsRes.data);
        setRecentStudents(studentsRes.data.students || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    { 
      title: t('dash_active_students', lang), 
      value: stats?.total_students || 0, 
      icon: Users, 
      color: 'bg-primary',
      trend: '+12%',
      trendUp: true
    },
    { 
      title: t('dash_active_courses', lang), 
      value: stats?.active_courses || 0, 
      icon: BookOpen, 
      color: 'bg-secondary',
      trend: '+3',
      trendUp: true
    },
    { 
      title: t('dash_dossiers', lang), 
      value: stats?.total_files || 0, 
      icon: FolderOpen, 
      color: 'bg-accent',
      trend: lang === 'de' ? '5 ausstehend' : '5 en attente',
      trendUp: null
    },
    { 
      title: t('dash_pending_payments', lang), 
      value: stats?.pending_payments || 0, 
      icon: CreditCard, 
      color: 'bg-warning',
      trend: '-2',
      trendUp: false
    },
  ];

  const quickActions = [
    { label: t('dash_create_student', lang), icon: UserPlus, onClick: () => navigate('/students'), color: 'bg-primary hover:bg-primary/90' },
    { label: t('dash_create_course', lang), icon: BookOpen, onClick: () => navigate('/courses'), color: 'bg-secondary hover:bg-secondary/90' },
    { label: t('dash_assign_partner', lang), icon: Handshake, onClick: () => navigate('/partners'), color: 'bg-accent hover:bg-accent/90' },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'de' ? 'de-DE' : 'fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      {/* Stats Grid - 4 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="card-hover border-0 shadow-sm animate-slide-up"
            style={{ animationDelay: `${index * 0.05}s` }}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold font-heading">{stat.value}</p>
                  {stat.trend && (
                    <div className={`flex items-center gap-1 text-xs font-medium ${
                      stat.trendUp === true ? 'text-success' : 
                      stat.trendUp === false ? 'text-destructive' : 
                      'text-muted-foreground'
                    }`}>
                      {stat.trendUp === true && <TrendingUp className="w-3 h-3" />}
                      {stat.trendUp === false && <TrendingDown className="w-3 h-3" />}
                      <span>{stat.trend}</span>
                    </div>
                  )}
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center shrink-0`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Two columns section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Dernières inscriptions */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <div>
              <CardTitle className="font-heading text-xl">{t('dash_recent_enrollments', lang)}</CardTitle>
              <CardDescription>{lang === 'de' ? 'Die 5 letzten eingeschriebenen Studierenden' : 'Les 5 derniers étudiants inscrits'}</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/students')}
              data-testid="view-all-students-btn"
            >
              {lang === 'de' ? 'Alle anzeigen' : 'Voir tout'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentStudents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>Aucun étudiant pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentStudents.map((student) => (
                  <div 
                    key={student.id} 
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/students')}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {student.first_name[0]}{student.last_name[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {student.first_name} {student.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Niveau {student.german_level}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">
                        {formatDate(student.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Actions rapides */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl">{t('dash_quick_actions', lang)}</CardTitle>
            <CardDescription>{lang === 'de' ? 'Schnellzugriff auf die Hauptfunktionen' : 'Accédez rapidement aux fonctionnalités principales'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                onClick={action.onClick}
                className={`w-full justify-start h-14 text-left ${action.color} text-white btn-active`}
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <action.icon className="w-5 h-5 mr-3" />
                <span className="font-medium">{action.label}</span>
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
